import React, { useEffect, useState } from "react";
import { Bookmark, Heart, BookOpen, Mail, MailX, LogOut, Check } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const mediaSrc = (url) => (url && url.startsWith("http") ? url : `${API_URL}${url}`);

export default function ReaderProfile({ reader, darkMode, onOpenArticle, onSignOut }) {
  const [essays, setEssays] = useState([]);
  const [subscribed, setSubscribed] = useState(null);
  const [toggling, setToggling] = useState(false);
  const [following, setFollowing] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/articles`).then((r) => r.json()).then((d) => setEssays(d.articles || [])).catch(() => {});
    fetch(`${API_URL}/api/reader/newsletter`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => setSubscribed(d.subscribed))
      .catch(() => setSubscribed(false));
    fetch(`${API_URL}/api/reader/follows`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => setFollowing(d.following || []))
      .catch(() => setFollowing([]));
  }, []);

  const unfollow = async (target) => {
    try {
      const res = await fetch(`${API_URL}/api/reader/follow`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ target, following: false }),
      });
      if (!res.ok) throw new Error();
      setFollowing((c) => (c || []).filter((t) => t !== target));
      toast.success("Unfollowed.");
    } catch {
      toast.error("Could not update your follow right now.");
    }
  };

  const toggleNewsletter = async () => {
    setToggling(true);
    try {
      const res = await fetch(`${API_URL}/api/reader/newsletter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ subscribed: !subscribed }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error();
      setSubscribed(data.subscribed);
      toast.success(data.subscribed ? "The Mizan Dispatch will find your inbox." : "You are unsubscribed from the Dispatch.");
    } catch {
      toast.error("Could not update your subscription.");
    }
    setToggling(false);
  };

  const byId = Object.fromEntries(essays.map((a) => [a.id, a]));
  const saved = (reader.bookmarks || []).map((id) => byId[id]).filter(Boolean);
  const liked = (reader.likes || []).map((id) => byId[id]).filter(Boolean);
  const history = (reader.history || []).map((id) => byId[id]).filter(Boolean);

  const cardCls = `rounded-2xl border p-5 transition-all duration-300 hover:-translate-y-0.5 cursor-pointer ${darkMode ? "bg-[#181C22] border-[#2A2F35] hover:border-[#B85D34]/50" : "bg-[#F4EFE6] border-[#E6DEC7] hover:border-[#B85D34]/50"}`;

  const EssayRow = ({ list, icon: Icon, emptyText, testid }) => (
    <section className="space-y-4">
      {list.length === 0 ? (
        <p className="text-sm opacity-55 font-light italic">{emptyText}</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {list.map((art) => (
            <div key={art.id} data-testid={`${testid}-${art.id}`} onClick={() => onOpenArticle(art.id)} className={cardCls}>
              <div className="flex gap-4 items-start">
                {art.cover && <img src={mediaSrc(art.cover)} alt="" className="w-16 h-16 rounded-xl object-cover shrink-0 border border-[#E6DEC7]" />}
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[#B85D34] font-semibold"><Icon size={11} /> {art.topic}</div>
                  <h4 className="font-serif text-lg font-medium leading-snug mt-1 truncate">{art.title}</h4>
                  <p className="text-xs opacity-60 mt-1">{art.date} · {art.readTime}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );

  return (
    <div data-testid="reader-profile" className="max-w-3xl mx-auto space-y-12 animate-fade-in pb-16">
      <div className="space-y-3 text-center">
        <span className="text-xs uppercase tracking-[0.2em] text-[#B85D34] font-semibold">Personal Library</span>
        <h1 className="font-serif text-4xl lg:text-5xl font-normal">Your quiet shelves</h1>
        <p className="opacity-80 max-w-xl mx-auto font-light">Everything you have saved, loved, and read, kept here for your return.</p>
      </div>

      {/* Account */}
      <section className={`rounded-3xl border p-7 flex flex-col sm:flex-row items-center gap-6 ${darkMode ? "bg-[#181C22] border-[#2A2F35]" : "bg-[#F4EFE6] border-[#E6DEC7]"}`}>
        {reader.picture ? (
          <img src={reader.picture} alt={reader.name} className="w-20 h-20 rounded-full border-2 border-[#B85D34]/40 object-cover" referrerPolicy="no-referrer" />
        ) : (
          <div className="w-20 h-20 rounded-full bg-[#B85D34]/10 border-2 border-[#B85D34]/40 flex items-center justify-center font-serif text-2xl text-[#B85D34]">{(reader.name || "R").slice(0, 1).toUpperCase()}</div>
        )}
        <div className="text-center sm:text-left flex-1">
          <h3 data-testid="reader-name" className="font-serif text-2xl font-medium">{reader.name}</h3>
          <p data-testid="reader-email" className="text-sm opacity-65">{reader.email}</p>
          <p className="text-xs opacity-55 mt-2 font-light">{(reader.bookmarks || []).length} saved · {(reader.likes || []).length} resonated · {(reader.history || []).length} read</p>
        </div>
        <button data-testid="profile-signout-btn" onClick={onSignOut} className="text-xs uppercase tracking-wider opacity-70 hover:text-[#B85D34] flex items-center gap-2 transition-colors"><LogOut size={14} /> Sign out</button>
      </section>

      {/* Following */}
      <section className={`rounded-3xl border p-7 space-y-4 ${darkMode ? "bg-[#181C22] border-[#2A2F35]" : "bg-[#F4EFE6] border-[#E6DEC7]"}`}>
        <h4 className="font-serif text-lg font-medium">Following</h4>
        {following === null ? (
          <p className="text-xs opacity-65">Checking...</p>
        ) : following.length === 0 ? (
          <p data-testid="following-empty" className="text-xs opacity-65 font-light">Not following anything yet. Follow the author or the publication from any essay to receive new essays by letter.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {following.map((target) => (
              <span key={target} data-testid={`following-${target}`} className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium ${darkMode ? "bg-[#2A2F35]" : "bg-white border border-[#E6DEC7]"}`}>
                {target === "author" ? "Shailla Khan" : "Mind & Mizan"}
                <button data-testid={`unfollow-${target}`} onClick={() => unfollow(target)} className="opacity-50 hover:opacity-100 hover:text-[#B85D34] transition-all" aria-label={`Unfollow ${target}`}>✕</button>
              </span>
            ))}
          </div>
        )}
      </section>

      {/* Newsletter status */}
      <section className={`rounded-3xl border p-7 flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${darkMode ? "bg-[#181C22] border-[#2A2F35]" : "bg-[#F4EFE6] border-[#E6DEC7]"}`}>
        <div className="flex items-start gap-4">
          <div className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 ${subscribed ? "bg-[#B85D34] text-white" : darkMode ? "bg-[#2A2F35]" : "bg-[#E6DEC7]"}`}>
            {subscribed ? <Mail size={17} /> : <MailX size={17} />}
          </div>
          <div>
            <h4 className="font-serif text-lg font-medium">The Mizan Dispatch</h4>
            <p data-testid="newsletter-status" className="text-xs opacity-65 mt-1">
              {subscribed === null ? "Checking your subscription..." : subscribed ? "Subscribed, one essay arrives each Friday morning." : "Not subscribed, join for one quiet essay each Friday."}
            </p>
          </div>
        </div>
        {subscribed !== null && (
          <button data-testid="newsletter-toggle-btn" onClick={toggleNewsletter} disabled={toggling} className={`px-5 py-2.5 rounded-full text-xs font-medium transition-all disabled:opacity-50 ${subscribed ? "border border-[#E6DEC7] dark:border-[#3A4048] hover:border-[#B85D34] hover:text-[#B85D34]" : "bg-[#B85D34] text-white hover:bg-[#A04E29]"}`}>
            {toggling ? "Updating..." : subscribed ? "Unsubscribe" : "Subscribe"}
          </button>
        )}
      </section>

      {/* Shelves */}
      <div className="space-y-10">
        <div className="space-y-4">
          <h3 className="font-serif text-2xl font-normal flex items-center gap-3"><Bookmark size={18} className="text-[#B85D34]" /> Saved essays</h3>
          <EssayRow list={saved} icon={Bookmark} emptyText="No saved essays yet, your sanctuary awaits its first volume." testid="profile-saved" />
        </div>
        <div className="space-y-4">
          <h3 className="font-serif text-2xl font-normal flex items-center gap-3"><Heart size={18} className="text-[#B85D34]" /> Resonated with you</h3>
          <EssayRow list={liked} icon={Heart} emptyText="Essays you mark with resonance will gather here." testid="profile-liked" />
        </div>
        <div className="space-y-4">
          <h3 className="font-serif text-2xl font-normal flex items-center gap-3"><BookOpen size={18} className="text-[#B85D34]" /> Reading history</h3>
          <EssayRow list={history} icon={Check} emptyText="Essays you open will be remembered here." testid="profile-history" />
        </div>
      </div>
    </div>
  );
}
